
export class Sequencer {
    constructor(audioContext, onNote) {
        this.ctx = audioContext;
        this.onNote = onNote; // Callback(freq, velocity, gateLength)

        // Timing
        this.isPlaying = false;
        this.bpm = 120;
        this.lookahead = 25.0; // ms
        this.scheduleAheadTime = 0.1; // s
        this.nextNoteTime = 0.0;
        this.timerID = null;
        
        // Sequencer State
        this.current16thNote = 0; // 0-15
        this.steps = Array(16).fill(null).map(() => ({
            active: false,
            pitch: 0, // Semitone offset from C4 (60) or relative? Let's say MIDI note 60 center.
            velocity: 0.7,
            gate: 0.5 // 0-1 duration
        }));

        // Arpeggiator State
        this.arpEnabled = false;
        this.arpMode = 'up'; // up, down, updown, random, chord
        this.arpHeldNotes = []; // Array of MIDI note numbers
        this.arpIndex = 0;
        
        // Event Hooks for UI
        this.onStepChange = null; // Callback(stepIndex) for UI highlighting
    }

    start() {
        if (this.isPlaying) return;
        
        if (this.ctx.state === 'suspended') {
            this.ctx.resume();
        }

        this.isPlaying = true;
        this.current16thNote = 0;
        this.arpIndex = 0;
        this.nextNoteTime = this.ctx.currentTime + 0.05; // Start slightly in future
        
        this.timerID = setInterval(() => this.scheduler(), this.lookahead);
    }

    stop() {
        this.isPlaying = false;
        clearInterval(this.timerID);
        this.timerID = null;
        // Reset specific state if needed, but keeping currentStep allows "pause" behavior if desired.
        // For hard stop:
        this.current16thNote = 0;
        if (this.onStepChange) this.onStepChange(0);
    }

    toggle() {
        if (this.isPlaying) this.stop();
        else this.start();
    }

    scheduler() {
        // While there are notes that will need to play before the next interval, 
        // schedule them and advance the pointer.
        while (this.nextNoteTime < this.ctx.currentTime + this.scheduleAheadTime) {
            this.scheduleNote(this.current16thNote, this.nextNoteTime);
            this.nextStep();
        }
    }

    nextStep() {
        const secondsPerBeat = 60.0 / this.bpm;
        this.nextNoteTime += 0.25 * secondsPerBeat; // 16th notes
        
        this.current16thNote++;
        if (this.current16thNote === 16) {
            this.current16thNote = 0;
        }
    }

    scheduleNote(beatNumber, time) {
        if (this.onStepChange) {
            this.onStepChange(beatNumber);
        }

        if (this.arpEnabled && this.arpHeldNotes.length > 0) {
            this.playArpStep(time);
        } else if (!this.arpEnabled) {
            this.playSeqStep(beatNumber, time);
        }
    }

    playSeqStep(stepIndex, time) {
        const step = this.steps[stepIndex];
        if (step.active) {
            // Default Root: C4 (60)
            const note = 60 + (step.pitch || 0); 
            const freq = this.midiToFreq(note);
            const duration = (60 / this.bpm) * 0.25 * step.gate;
            
            this.onNote(freq, step.velocity, duration, time, 'seq');
        }
    }

    playArpStep(time) {
        if (this.arpHeldNotes.length === 0) return;

        // Sort notes for consistent arpeggiation
        const sortedNotes = [...this.arpHeldNotes].sort((a, b) => a - b);
        let noteToPlay = null;

        if (this.arpMode === 'chord') {
            // Play all
            sortedNotes.forEach((note, i) => {
                const freq = this.midiToFreq(note);
                // Standard gate for arp?
                const duration = (60 / this.bpm) * 0.25 * 0.8; 
                this.onNote(freq, 0.7, duration, time, `arp-${i}`);
            });
            return;
        }

        // Logic for next note
        if (this.arpMode === 'up') {
            noteToPlay = sortedNotes[this.arpIndex % sortedNotes.length];
            this.arpIndex++;
        } else if (this.arpMode === 'down') {
            // Reverse index logic or just access
            const idx = this.arpIndex % sortedNotes.length;
            noteToPlay = sortedNotes[sortedNotes.length - 1 - idx];
            this.arpIndex++;
        } else if (this.arpMode === 'random') {
            noteToPlay = sortedNotes[Math.floor(Math.random() * sortedNotes.length)];
        } else if (this.arpMode === 'updown') {
             // 0, 1, 2, 1, 0...
             const len = sortedNotes.length;
             if (len < 2) {
                 noteToPlay = sortedNotes[0];
             } else {
                const cycle = (len * 2) - 2; // e.g. 3 notes: 0,1,2,1 -> 4 steps
                const modStep = this.arpIndex % cycle;
                if (modStep < len) {
                    noteToPlay = sortedNotes[modStep];
                } else {
                    noteToPlay = sortedNotes[cycle - modStep]; // 3 -> 2-1 = 1? No.
                    // len=3. cycle=4.
                    // 0:0, 1:1, 2:2, 3:1. 4:0
                    // modStep=3. cycle-modStep = 4-3=1. Correct.
                }
             }
             this.arpIndex++;
        }

        if (noteToPlay !== null) {
            const freq = this.midiToFreq(noteToPlay);
            const duration = (60 / this.bpm) * 0.25 * 0.8;
            this.onNote(freq, 0.7, duration, time, 'arp');
        }
    }

    setArpMode(mode) {
        this.arpMode = mode;
        this.arpIndex = 0;
    }

    updateHeldNotes(notes) {
        // notes: Set or Array of MIDI numbers
        this.arpHeldNotes = Array.from(notes);
        // If no notes held, reset index?
        if (this.arpHeldNotes.length === 0) {
            this.arpIndex = 0;
        }
    }

    setBpm(bpm) {
        this.bpm = bpm;
    }

    setStep(index, active) {
        if (this.steps[index]) {
            this.steps[index].active = active;
        }
    }
    
    setStepData(index, data) {
        if (this.steps[index]) {
            Object.assign(this.steps[index], data);
        }
    }

    midiToFreq(note) {
        return 440 * Math.pow(2, (note - 69) / 12);
    }
}
